import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import client from "../../api/client";
import { apiUpload } from "../../utils/apiProxy";
import "./ApprovalPopup.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Состав книги. Сейчас статикой — в будущем редактируемые поля.
const DEFAULT_COMPOSITION = {
  format: "120×180",
  color: "4+4",
  binding: "тв.клей",
  lamination: "матовая",
  paper: "80гр",
};

export type ApprovalMode = "full" | "cover-only";

export interface ApprovalPopupProps {
  userId: number | string;
  username?: string;
  mode?: ApprovalMode;
  /** Если задан — слот «Книга» предзаполнен этим URL (например, ссылка из Spaces).
   *  При сохранении этот URL пишется в archive как есть (файл уже в storage). */
  prefillBookUrl?: string;
  /** Если задан — слот «Обложка» предзаполнен URL'ом. */
  prefillCoverUrl?: string;
  /** Если задан — в слот «Книга» заряжен File (например, blob, скачанный с
   *  /pdf/admin). При сохранении файл будет аплоднут в /api/upload. */
  initialBookFile?: File;
  /** Аналогично для обложки. */
  initialCoverFile?: File;
  /** Префилл количества страниц (например из /admin/page-count). */
  prefillPages?: number;
  onClose: () => void;
  onSaved?: (archive: any) => void;
}

interface PdfSlotProps {
  label: string;
  url: string | null;
  fileName?: string;
  onPick: (file: File) => void;
  disabled?: boolean;
}

// Файлы из Spaces браузер напрямую не читает (у CDN нет CORS) — превью
// тянем через бэк-прокси в blob. Локальные blob:-url отдаём как есть.
function useViewUrl(url: string | null): { view: string | null; failed: boolean } {
  const [view, setView] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    if (!url || url.startsWith("blob:")) {
      setView(url);
      return;
    }
    let cancelled = false;
    let made: string | null = null;
    setView(null);
    client
      .get("/admin/archives/proxy", { params: { url }, responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        made = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setView(made);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [url]);
  return { view, failed };
}

const PdfSlot: React.FC<PdfSlotProps> = ({ label, url, fileName, onPick, disabled }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { view, failed } = useViewUrl(url);

  return (
    <div className={`ap-slot ${disabled ? "ap-slot-disabled" : ""}`}>
      <div className="ap-slot-head">
        <span className="ap-slot-label">{label}</span>
        {fileName && <span className="ap-slot-filename" title={fileName}>{fileName}</span>}
      </div>
      <button
        type="button"
        className="ap-slot-thumb"
        onClick={() => !disabled && inputRef.current?.click()}
        disabled={disabled}
      >
        {failed ? (
          <div className="ap-slot-empty">не открылся PDF</div>
        ) : url && !view ? (
          <div className="ap-slot-empty">загрузка…</div>
        ) : view ? (
          <Document
            file={view}
            loading={<div className="ap-slot-empty">загрузка…</div>}
            error={<div className="ap-slot-empty">не открылся PDF</div>}
          >
            <Page
              pageNumber={1}
              width={220}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </Document>
        ) : (
          <div className="ap-slot-empty">+ выбрать PDF</div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </div>
  );
};

export const ApprovalPopup: React.FC<ApprovalPopupProps> = ({
  userId,
  username,
  mode = "full",
  prefillBookUrl,
  prefillCoverUrl,
  initialBookFile,
  initialCoverFile,
  prefillPages,
  onClose,
  onSaved,
}) => {
  // Локальные blob-url'ы для превью только что выбранных файлов; чистим в unmount.
  const [bookFile, setBookFile] = useState<File | null>(initialBookFile ?? null);
  const [coverFile, setCoverFile] = useState<File | null>(initialCoverFile ?? null);
  const [bookUrl, setBookUrl] = useState<string | null>(
    initialBookFile ? URL.createObjectURL(initialBookFile) : prefillBookUrl ?? null
  );
  const [coverUrl, setCoverUrl] = useState<string | null>(
    initialCoverFile ? URL.createObjectURL(initialCoverFile) : prefillCoverUrl ?? null
  );
  const [pages, setPages] = useState<string>(prefillPages ? String(prefillPages) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Если для юзера уже есть архив — переутверждение. Запоминаем, чтобы
  // лейбл кнопки и copy в шапке отражали это.
  const [hasExisting, setHasExisting] = useState(false);
  // Название книги и автор (правка редактуры приоритетнее оригинала) —
  // показываются в шапке под username.
  const [bookMeta, setBookMeta] = useState<{ title: string; author: string } | null>(null);

  const showBookSlot = mode === "full";

  useEffect(() => {
    return () => {
      // Освобождаем blob-url'ы созданные через URL.createObjectURL для
      // только что выбранных локальных файлов (prefill URLs не наши, не трогаем).
      if (bookFile && bookUrl) URL.revokeObjectURL(bookUrl);
      if (coverFile && coverUrl) URL.revokeObjectURL(coverUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // На маунте подтягиваем последний архив юзера. Если он есть — это сценарий
  // переутверждения, префилим пустые поля текущими значениями. Префилы из
  // пропсов (например, файл из /pdf/admin) имеют приоритет.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get("/admin/archives/latest", {
          params: { userId },
        });
        if (cancelled) return;
        if (!data) {
          // Архива нет — подставляем то, что клиенту скинули на утверждение.
          const { data: info } = await client.get("/proofs/admin", { params: { userId } });
          const proof = (info?.proofs || []).find((p: any) => p.status !== "superseded");
          if (cancelled || !proof) return;
          if (!coverUrl && proof.coverPdfUrl) setCoverUrl(proof.coverPdfUrl);
          if (mode === "full" && !bookUrl && proof.bookPdfUrl) setBookUrl(proof.bookPdfUrl);
          if (!pages && proof.pages) setPages(String(proof.pages));
          return;
        }
        setHasExisting(true);
        if (!coverUrl && data.coverPdfUrl) setCoverUrl(data.coverPdfUrl);
        if (mode === "full" && !bookUrl && data.bookPdfUrl) setBookUrl(data.bookPdfUrl);
        if (!pages && data.pages) setPages(String(data.pages));
      } catch (err) {
        console.error("[approval] fetch latest failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Название книги и автор из covers — как в генерации: правка редактуры
  // (edited_*) приоритетнее оригинала юзера.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/admin/cover/${userId}`);
        if (cancelled || !data) return;
        setBookMeta({
          title: data.editedBookName || data.bookName || "",
          author: data.editedAuthorName || data.authorName || "",
        });
      } catch (err) {
        // 404 = обложки ещё нет — просто не показываем строку.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleBookPick = (f: File) => {
    if (bookFile && bookUrl) URL.revokeObjectURL(bookUrl);
    setBookFile(f);
    setBookUrl(URL.createObjectURL(f));
  };
  const handleCoverPick = (f: File) => {
    if (coverFile && coverUrl) URL.revokeObjectURL(coverUrl);
    setCoverFile(f);
    setCoverUrl(URL.createObjectURL(f));
  };

  const composition = useMemo(() => DEFAULT_COMPOSITION, []);

  // Аплоад нового файла: возвращает url из /api/upload. Если файл не выбран,
  // отдаём prefillUrl как есть — он уже лежит в storage / на нашем бэке.
  const uploadIfNeeded = async (file: File | null, prefill?: string): Promise<string | null> => {
    if (!file) return prefill || null;
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiUpload("/upload", fd);
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    const data = await res.json();
    return data?.url ?? null;
  };

  const handleSave = async () => {
    setError(null);
    // Без выбранного файла — то, что стоит в слоте: префилл из пропсов, архив
    // или отправка клиенту (уже лежат в storage). blob: — только превью файла.
    const remote = (u: string | null) => (u && !u.startsWith("blob:") ? u : undefined);
    const prefillCoverUrl = remote(coverUrl);
    const prefillBookUrl = remote(bookUrl);

    if (mode === "cover-only") {
      if (!coverFile && !prefillCoverUrl) {
        setError("Загрузите PDF обложки");
        return;
      }
    } else {
      if (!coverFile && !prefillCoverUrl && !bookFile && !prefillBookUrl) {
        setError("Загрузите хотя бы один PDF");
        return;
      }
    }

    setSaving(true);
    try {
      const finalCoverUrl = await uploadIfNeeded(coverFile, prefillCoverUrl);
      const finalBookUrl =
        mode === "cover-only" ? null : await uploadIfNeeded(bookFile, prefillBookUrl);

      const { data } = await client.post("/admin/archives", {
        userId,
        coverPdfUrl: finalCoverUrl,
        bookPdfUrl: finalBookUrl,
        pages: Number(pages) || 0,
        composition,
        // express не передаём — бэк сам копирует текущий snapshot orders.express
      });
      onSaved?.(data);
      onClose();
    } catch (err: any) {
      console.error("[approval] save failed", err);
      setError(err?.response?.data?.error || err?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-overlay" onClick={onClose}>
      <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ap-head">
          <div>
            <h3 className="ap-title">
              {mode === "cover-only"
                ? hasExisting
                  ? "Переутвердить обложку"
                  : "Утвердить обложку"
                : hasExisting
                  ? "Переутвердить книгу"
                  : "Утвердить книгу"}
            </h3>
            {username && <div className="ap-subtitle">{username}</div>}
            {bookMeta && (bookMeta.title || bookMeta.author) && (
              <div className="ap-bookmeta">
                {bookMeta.title && <span className="ap-bookmeta-title">«{bookMeta.title}»</span>}
                {bookMeta.title && bookMeta.author && " — "}
                {bookMeta.author}
              </div>
            )}
          </div>
          <button className="ap-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className="ap-slots">
          <PdfSlot
            label="Обложка"
            url={coverUrl}
            fileName={coverFile?.name}
            onPick={handleCoverPick}
          />
          <PdfSlot
            label="Книга"
            url={bookUrl}
            fileName={bookFile?.name}
            onPick={handleBookPick}
            disabled={!showBookSlot}
          />
        </div>

        <div className="ap-fields">
          <label className="ap-field">
            <span className="ap-field-label">Кол-во страниц</span>
            <input
              type="number"
              value={pages}
              onChange={(e) => setPages(e.target.value)}
              placeholder="0"
            />
          </label>
        </div>

        <div className="ap-composition">
          <div className="ap-composition-title">Состав</div>
          <div className="ap-composition-chips">
            <span className="ap-chip">{composition.format}</span>
            <span className="ap-chip">{composition.color}</span>
            <span className="ap-chip">{composition.binding}</span>
            <span className="ap-chip">лам. {composition.lamination}</span>
            <span className="ap-chip">{composition.paper}</span>
          </div>
        </div>

        {error && <div className="ap-error">{error}</div>}

        <div className="ap-actions">
          <button className="ap-btn-ghost" onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button className="ap-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Сохранение…" : hasExisting ? "Переутвердить" : "Утвердить"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalPopup;
